// @flow strict
import React from 'react';
import styledImport from 'styled-components';
const styled = styledImport.default || styledImport;
import Navbar from '../../components/Navbar/index.js';
import Footer from '../../components/Footer/index.js';
import Text from '../../components/Text/index.js';
import Link from '../../components/Link/index.js';
import { container } from '../../services/styleUtils/index.js';
import mq from '../../services/mediaQuery/index.js';

const GITHUB_URL = 'https://github.com/ticketlens/ticketlens-experiences-mcp';
const API_DOCS_URL = 'https://api.ticketlens.com/v1/docs';
const MCP_URL = 'https://mcp.ticketlens.com/';
const OPENAPI_URL = 'https://api.ticketlens.com/v1/openapi.json';

const HERO_POINTS = [
  'ai_hero_point_inventory',
  'ai_hero_point_protocols',
  'ai_hero_point_examples',
];

const NEED_CARDS = [
  {
    title: 'ai_need_poi_title',
    body: 'ai_need_poi_body',
  },
  {
    title: 'ai_need_search_title',
    body: 'ai_need_search_body',
  },
  {
    title: 'ai_need_detail_title',
    body: 'ai_need_detail_body',
  },
];

const PROVIDES_KEYS = [
  'ai_provides_search',
  'ai_provides_pois',
  'ai_provides_detail',
  'ai_provides_inventory',
];

const COVERED_KEYS = [
  'ai_covered_attractions',
  'ai_covered_tours',
  'ai_covered_buses',
  'ai_covered_sports',
  'ai_covered_events',
];

const DOC_CARDS = [
  {
    title: 'ai_docs_repo_title',
    body: 'ai_docs_repo_body',
    links: [
      {
        href: GITHUB_URL,
        label: 'github.com/ticketlens/ticketlens-experiences-mcp',
      },
    ],
  },
  {
    title: 'ai_docs_api_title',
    body: 'ai_docs_api_body',
    links: [
      {
        href: API_DOCS_URL,
        label: 'api.ticketlens.com/v1/docs',
      },
      {
        href: OPENAPI_URL,
        label: 'api.ticketlens.com/v1/openapi.json',
      },
    ],
  },
  {
    title: 'ai_docs_examples_title',
    body: 'ai_docs_examples_body',
    links: [
      {
        href: `${GITHUB_URL}/tree/main/examples`,
        label: 'github.com/.../examples',
      },
    ],
  },
];

const MCP_EXAMPLE = `codex mcp add ticketlens-experiences --url https://mcp.ticketlens.com/`;

const API_EXAMPLE = `curl -sS https://api.ticketlens.com/v1/search/tours \\
  -H 'content-type: application/json' \\
  -d '{
    "query": "Eiffel Tower ticket",
    "city": "Paris",
    "languages": ["en"],
    "per_page": 5
  }'`;

const PageRoot = styled.div`
  background: ${({ theme }) => theme.trip.backgroundLight};
`;

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at top right, rgba(255, 255, 255, 0.12), transparent 28%),
    linear-gradient(135deg, ${({ theme }) => theme.secondary} 0%, #7f1044 100%);
  color: ${({ theme }) => theme.tertiary};
`;

const HeroContainer = styled.div`
  ${container()};
  padding: 34px 15.5px 44px;

  ${mq.TABLET`
    padding: 48px 30px 58px;
  `}

  ${mq.DESKTOP`
    padding: 58px 30px 70px;
  `}
`;

const HeroGrid = styled.div`
  display: grid;
  gap: 24px;

  ${mq.DESKTOP`
    grid-template-columns: minmax(0, 1.45fr) minmax(290px, 0.85fr);
    gap: 30px;
    align-items: start;
  `}
`;

const Eyebrow = styled.div`
  display: inline-flex;
  margin-bottom: 14px;
  padding: 7px 12px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: 34px;
  line-height: 1.05;
  letter-spacing: -0.03em;

  ${mq.TABLET`
    font-size: 48px;
  `}

  ${mq.DESKTOP`
    font-size: 58px;
    max-width: 11ch;
  `}
`;

const HeroSubtitle = styled.p`
  max-width: 700px;
  margin: 16px 0 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 18px;
  line-height: 1.55;

  ${mq.TABLET`
    font-size: 21px;
  `}
`;

const CtaRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
  margin-top: 24px;

  ${mq.TABLET`
    display: flex;
    flex-wrap: wrap;
  `}
`;

const CtaLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  padding: 13px 18px;
  border: 2px solid
    ${({ $secondary, $lightSection, theme }) => {
      if ($lightSection) {
        return theme.secondary;
      }
      return $secondary ? 'rgba(255, 255, 255, 0.42)' : theme.tertiary;
    }};
  border-radius: 18px;
  background: ${({ $secondary, $lightSection, theme }) => {
    if ($lightSection) {
      return $secondary ? 'rgba(197, 17, 98, 0.05)' : theme.secondary;
    }
    return $secondary ? 'transparent' : theme.tertiary;
  }};
  color: ${({ $secondary, $lightSection, theme }) => {
    if ($lightSection) {
      return $secondary ? theme.secondary : theme.tertiary;
    }
    return $secondary ? theme.tertiary : theme.secondary;
  }};
  box-sizing: border-box;
  font-weight: 900;
  text-align: center;
  transition: transform 0.2s ease-in, background 0.2s ease-in, box-shadow 0.2s ease-in;

  ${mq.TABLET`
    width: auto;
    min-width: 220px;
    border-radius: 999px;
  `}

  ${mq.DESKTOP`
    &:hover {
      transform: translateY(-2px);
      text-decoration: none;
    }
  `}
`;

const HeroPanel = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 18px;
  background: rgba(24, 7, 16, 0.22);
  backdrop-filter: blur(7px);
`;

const HeroPanelInner = styled.div`
  padding: 20px;

  ${mq.TABLET`
    padding: 24px;
  `}
`;

const HeroPanelTitle = styled.h2`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.tertiary};
  font-size: 20px;
  line-height: 1.3;
`;

const HeroList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const HeroListItem = styled.li`
  position: relative;
  padding-left: 18px;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.6;

  & + & {
    margin-top: 10px;
  }

  &:before {
    position: absolute;
    top: 10px;
    left: 0;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.tertiary};
    content: '';
  }
`;

const Section = styled.section`
  padding: 28px 0;

  ${mq.TABLET`
    padding: 38px 0;
  `}

  ${mq.DESKTOP`
    padding: 46px 0;
  `}
`;

const SectionAlt = styled(Section)`
  background: ${({ theme }) => theme.tertiary};
`;

const SectionContainer = styled.div`
  ${container()};
  padding: 0 15.5px;

  ${mq.TABLET`
    padding: 0 30px;
  `}
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.section.title};
  font-size: 30px;
  line-height: 1.15;

  ${mq.TABLET`
    font-size: 38px;
  `}
`;

const SectionIntro = styled.p`
  max-width: 760px;
  margin: 14px 0 0;
  color: ${({ theme }) => theme.section.abstract};
  font-size: 18px;
  line-height: 1.65;
`;

const CardGrid = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 24px;

  ${mq.TABLET`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}

  ${mq.DESKTOP`
    grid-template-columns: repeat(3, minmax(0, 1fr));
  `}
`;

const SurfaceStack = styled.div`
  margin-top: 24px;
`;

const Surface = styled.article`
  box-sizing: border-box;
  height: 100%;
  min-width: 0;
  padding: 22px 20px;
  border: 1px solid ${({ theme }) => theme.trip.divider};
  border-radius: 18px;
  background: ${({ theme }) => theme.tertiary};
  box-shadow: 0 12px 30px rgba(51, 37, 50, 0.04);
`;

const AccentSurface = styled(Surface)`
  background:
    linear-gradient(180deg, rgba(197, 17, 98, 0.06), rgba(197, 17, 98, 0.02)),
    ${({ theme }) => theme.tertiary};
`;

const SurfaceTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.section.title};
  font-size: 22px;
  line-height: 1.25;
`;

const SurfaceText = styled.p`
  margin: 12px 0 0;
  color: ${({ theme }) => theme.section.abstract};
  line-height: 1.65;
`;

const PlainList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const PlainListItem = styled.li`
  position: relative;
  padding-left: 18px;
  color: ${({ theme }) => theme.section.abstract};
  line-height: 1.65;

  & + & {
    margin-top: 8px;
  }

  &:before {
    position: absolute;
    top: 10px;
    left: 0;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.secondary};
    content: '';
  }
`;

const CoverageGrid = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 24px;

  ${mq.TABLET`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}

  ${mq.DESKTOP`
    grid-template-columns: repeat(5, minmax(0, 1fr));
  `}
`;

const CoveragePill = styled.div`
  padding: 16px 18px;
  border-radius: 16px;
  background: ${({ theme }) => theme.tertiary};
  color: ${({ theme }) => theme.section.title};
  font-weight: 700;
  line-height: 1.45;
  box-shadow: 0 10px 24px rgba(51, 37, 50, 0.04);
`;

const AccessGrid = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 24px;

  ${mq.DESKTOP`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}
`;

const Label = styled.div`
  margin-top: 18px;
  color: ${({ theme }) => theme.section.description};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const MonoList = styled.ul`
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
`;

const MonoListItem = styled.li`
  min-width: 0;

  & + & {
    margin-top: 8px;
  }
`;

const Mono = styled.code`
  display: inline-block;
  max-width: 100%;
  font-family: SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
`;

const CodeBlock = styled.pre`
  overflow-x: auto;
  margin: 10px 0 0;
  width: 100%;
  max-width: 100%;
  padding: 16px;
  box-sizing: border-box;
  border-radius: 16px;
  background: #20141c;
  color: ${({ theme }) => theme.tertiary};
  font-family: SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.55;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-wrap;
`;

const ResourceLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 18px;
`;

const ResourceLink = styled(Link)`
  color: ${({ theme }) => theme.secondary};
  font-weight: 700;
  word-break: break-word;

  ${mq.DESKTOP`
    &:hover {
      text-decoration: underline;
    }
  `}
`;

const StartSection = styled(Section)`
  padding-top: 10px;
`;

const AiPage = () => (
  <PageRoot>
    <Navbar search={false} />
    <Hero>
      <HeroContainer>
        <HeroGrid>
          <div>
            <Eyebrow>
              <Text t="ai_hero_eyebrow" />
            </Eyebrow>
            <HeroTitle>
              <Text t="ai_hero_title" />
            </HeroTitle>
            <HeroSubtitle>
              <Text t="ai_hero_subtitle" />
            </HeroSubtitle>
            <CtaRow>
              <CtaLink href={GITHUB_URL} target="_blank" rel="noopener noreferrer">
                <Text t="ai_cta_github" />
              </CtaLink>
              <CtaLink href={API_DOCS_URL} target="_blank" rel="noopener noreferrer" $secondary>
                <Text t="ai_cta_docs" />
              </CtaLink>
              <CtaLink href={MCP_URL} target="_blank" rel="noopener noreferrer" $secondary>
                <Text t="ai_cta_mcp" />
              </CtaLink>
            </CtaRow>
          </div>
          <HeroPanel>
            <HeroPanelInner>
              <HeroPanelTitle>
                <Text t="ai_section_provides" />
              </HeroPanelTitle>
              <HeroList>
                {HERO_POINTS.map(key => (
                  <HeroListItem key={key}>
                    <Text t={key} />
                  </HeroListItem>
                ))}
              </HeroList>
            </HeroPanelInner>
          </HeroPanel>
        </HeroGrid>
      </HeroContainer>
    </Hero>

    <SectionAlt>
      <SectionContainer>
        <SectionTitle>
          <Text t="ai_section_why" />
        </SectionTitle>
        <SectionIntro>
          <Text t="ai_section_why_intro" />
        </SectionIntro>
        <CardGrid>
          {NEED_CARDS.map(card => (
            <AccentSurface key={card.title}>
              <SurfaceTitle>
                <Text t={card.title} />
              </SurfaceTitle>
              <SurfaceText>
                <Text t={card.body} />
              </SurfaceText>
            </AccentSurface>
          ))}
        </CardGrid>
      </SectionContainer>
    </SectionAlt>

    <Section>
      <SectionContainer>
        <SectionTitle>
          <Text t="ai_section_provides" />
        </SectionTitle>
        <SectionIntro>
          <Text t="ai_section_provides_intro" />
        </SectionIntro>
        <SurfaceStack>
          <Surface>
            <PlainList>
              {PROVIDES_KEYS.map(key => (
                <PlainListItem key={key}>
                  <Text t={key} />
                </PlainListItem>
              ))}
            </PlainList>
          </Surface>
        </SurfaceStack>
      </SectionContainer>
    </Section>

    <SectionAlt>
      <SectionContainer>
        <SectionTitle>
          <Text t="ai_section_covered" />
        </SectionTitle>
        <CoverageGrid>
          {COVERED_KEYS.map(key => (
            <CoveragePill key={key}>
              <Text t={key} />
            </CoveragePill>
          ))}
        </CoverageGrid>
      </SectionContainer>
    </SectionAlt>

    <Section>
      <SectionContainer>
        <SectionTitle>
          <Text t="ai_section_access" />
        </SectionTitle>
        <SectionIntro>
          <Text t="ai_section_access_intro" />
        </SectionIntro>
        <AccessGrid>
          <Surface>
            <SurfaceTitle>
              <Text t="ai_access_mcp_title" />
            </SurfaceTitle>
            <SurfaceText>
              <Text t="ai_access_mcp_body" />
            </SurfaceText>
            <Label>
              <Text t="ai_access_tools_label" />
            </Label>
            <MonoList>
              <MonoListItem>
                <Mono>search_tours</Mono>
              </MonoListItem>
              <MonoListItem>
                <Mono>search_pois</Mono>
              </MonoListItem>
              <MonoListItem>
                <Mono>get_tour</Mono>
              </MonoListItem>
              <MonoListItem>
                <Mono>health_check</Mono>
              </MonoListItem>
            </MonoList>
            <Label>
              <Text t="ai_mcp_example_label" />
            </Label>
            <CodeBlock>{MCP_EXAMPLE}</CodeBlock>
          </Surface>

          <Surface>
            <SurfaceTitle>
              <Text t="ai_access_api_title" />
            </SurfaceTitle>
            <SurfaceText>
              <Text t="ai_access_api_body" />
            </SurfaceText>
            <Label>
              <Text t="ai_access_endpoints_label" />
            </Label>
            <MonoList>
              <MonoListItem>
                <Mono>POST /v1/search/tours</Mono>
              </MonoListItem>
              <MonoListItem>
                <Mono>GET /v1/tours/{'{tour_id}'}</Mono>
              </MonoListItem>
              <MonoListItem>
                <Mono>POST /v1/search/pois</Mono>
              </MonoListItem>
              <MonoListItem>
                <Mono>GET /v1/pois/{'{poi_id}'}</Mono>
              </MonoListItem>
            </MonoList>
            <Label>
              <Text t="ai_api_example_label" />
            </Label>
            <CodeBlock>{API_EXAMPLE}</CodeBlock>
          </Surface>
        </AccessGrid>
      </SectionContainer>
    </Section>

    <SectionAlt>
      <SectionContainer>
        <SectionTitle>
          <Text t="ai_section_docs" />
        </SectionTitle>
        <SectionIntro>
          <Text t="ai_docs_intro" />
        </SectionIntro>
        <CardGrid>
          {DOC_CARDS.map(card => (
            <Surface key={card.title}>
              <SurfaceTitle>
                <Text t={card.title} />
              </SurfaceTitle>
              <SurfaceText>
                <Text t={card.body} />
              </SurfaceText>
              <ResourceLinks>
                {card.links.map(link => (
                  <ResourceLink
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </ResourceLink>
                ))}
              </ResourceLinks>
            </Surface>
          ))}
        </CardGrid>
      </SectionContainer>
    </SectionAlt>

    <StartSection>
      <SectionContainer>
        <SectionTitle>
          <Text t="ai_section_start" />
        </SectionTitle>
        <SectionIntro>
          <Text t="ai_start_body" />
        </SectionIntro>
        <CtaRow>
          <CtaLink
            href={GITHUB_URL}
            target="_blank"
            rel="noopener noreferrer"
            $lightSection
          >
            <Text t="ai_cta_github" />
          </CtaLink>
          <CtaLink
            href={API_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            $secondary
            $lightSection
          >
            <Text t="ai_cta_docs" />
          </CtaLink>
          <CtaLink
            href={MCP_URL}
            target="_blank"
            rel="noopener noreferrer"
            $secondary
            $lightSection
          >
            <Text t="ai_cta_mcp" />
          </CtaLink>
        </CtaRow>
      </SectionContainer>
    </StartSection>
    <Footer />
  </PageRoot>
);

export default AiPage;
